import React, { useState, useEffect } from 'react';
import { UploadCloud, Save, Trash2, Calendar, FileJson, FileCode } from 'lucide-react';
import toast from 'react-hot-toast';
import { parseTallyXmlText } from '../utils/tallyXmlImport';
import CreditEntryReviewTable from '../components/CreditEntryReviewTable';

const CreditSales = () => {
  const [entries, setEntries] = useState([]);
  const [savedEntries, setSavedEntries] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [isSaving, setIsSaving] = useState(false);
  const [creditors, setCreditors] = useState([]);
  const [xmlParty, setXmlParty] = useState('');

  useEffect(() => {
    fetchSavedEntries(selectedDate);
  }, [selectedDate]);

  useEffect(() => {
    fetch('/api/creditors', { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } })
      .then(res => res.json())
      .then(data => { if (!data.error) setCreditors(data); })
      .catch(err => console.error('Failed to fetch creditors', err));
  }, []);

  const fetchSavedEntries = async (date) => {
    try {
      const res = await fetch(`/api/credit-sales/${date}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      if (!data.error) {
        setSavedEntries(data);
      }
    } catch (error) {
      console.error("Failed to fetch saved entries", error);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const jsonData = JSON.parse(event.target.result);
        parseTallyData(jsonData);
      } catch (error) {
        toast.error('Invalid JSON file.');
        console.error("JSON parse error:", error);
      }
    };
    reader.readAsText(file);
    e.target.value = null; // Reset input
  };

  const parseTallyData = (data) => {
    try {
      const tallyMessages = data.tallymessage || [];
      const extracted = [];
      let parseError = false;

      tallyMessages.forEach((msg) => {
        // We only care about Sales vouchers
        if (msg.metadata && msg.metadata.vchtype === 'Sales') {
          const partyName = msg.partyname;
          const dateStr = msg.date; // e.g. "20260530"
          
          let formattedDate = selectedDate;
          if (dateStr && dateStr.length === 8) {
            formattedDate = `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`;
          }

          if (msg.allinventoryentries && Array.isArray(msg.allinventoryentries)) {
            msg.allinventoryentries.forEach(inv => {
              const itemName = inv.stockitemname || '';
              // amount might be negative in some contexts, but usually positive in inventory
              const amountStr = inv.amount || '0';
              const amount = Math.abs(parseFloat(amountStr.replace(/,/g, '')));
              
              const qtyStr = inv.billedqty || inv.actualqty || '';
              const qty = parseFloat(qtyStr.replace(/[^0-9.]/g, '')) || 0;

              extracted.push({
                id: Date.now() + Math.random(),
                business_date: formattedDate,
                party_name: partyName,
                item_name: itemName,
                quantity: qty,
                amount: amount
              });
            });
          }
        }
      });

      if (extracted.length > 0) {
        setEntries(extracted);
        toast.success(`Extracted ${extracted.length} sales entries.`);
      } else {
        toast.error('No sales entries found in this JSON.');
      }
    } catch (error) {
      toast.error('Error parsing Tally JSON structure.');
      console.error(error);
    }
  };

  const handleXmlUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!xmlParty) {
      toast.error('Pick which creditor this Tally export belongs to first.');
      e.target.value = null;
      return;
    }

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const { entries: extracted, error } = await parseTallyXmlText(event.target.result, xmlParty);
        if (error) {
          toast.error(error);
          return;
        }
        setEntries(extracted);
        toast.success(`Extracted ${extracted.length} entries from Tally XML.`);
      } catch (error) {
        toast.error('Error parsing Tally XML file.');
        console.error(error);
      }
    };
    // Tally's flat XML export is UTF-16
    reader.readAsText(file, 'utf-16le');
    e.target.value = null;
  };

  const handleEntryFieldChange = (id, field, value) => {
    setEntries(prev => prev.map(e => (e.id === id ? { ...e, [field]: value } : e)));
  };

  const handleSave = async () => {
    if (entries.length === 0) return;
    setIsSaving(true);
    
    // Group by date to save correctly if JSON has multiple dates
    // For simplicity, we just save them using the selectedDate or the date parsed from JSON.
    // In API, it expects { date, entries }
    try {
      const res = await fetch('/api/credit-sales', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          date: entries[0].business_date || selectedDate, // Use the first entry's date for simplicity, though backend just needs A date, we are sending the actual date in each entry anyway. Wait, API expects date at root.
          entries: entries
        })
      });
      const data = await res.json();
      
      if (data.status === 'success') {
        toast.success('Entries saved successfully!');
        setEntries([]);
        fetchSavedEntries(selectedDate);
      } else {
        toast.error(data.error || 'Failed to save entries.');
      }
    } catch (error) {
      toast.error('Failed to save entries.');
      console.error(error);
    }
    setIsSaving(false);
  };

  const deleteEntry = async (id) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    try {
      const res = await fetch(`/api/credit-sales/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        toast.success('Entry deleted');
        fetchSavedEntries(selectedDate);
      }
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col md:flex-row justify-between md:items-end gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Credit Sales Import</h1>
          <p className="text-slate-500 mt-1">Import JSON from Tally to track daily party sales.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="bg-white p-2 rounded-xl shadow-sm border border-slate-200 flex items-center gap-2">
            <Calendar className="text-slate-400" size={20} />
            <input 
              type="date" 
              className="outline-none text-slate-700 font-medium bg-transparent"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </div>
          <label className="btn-primary cursor-pointer flex items-center gap-2">
            <UploadCloud size={20} />
            Upload JSON
            <input
              type="file"
              accept=".json"
              className="hidden"
              onChange={handleFileUpload}
            />
          </label>
          <select
            className="bg-white p-2.5 rounded-xl shadow-sm border border-slate-200 text-sm font-medium text-slate-700"
            value={xmlParty}
            onChange={(e) => setXmlParty(e.target.value)}
          >
            <option value="">Which creditor is this XML for?</option>
            {creditors.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
          <label className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm shadow-sm ${xmlParty ? 'bg-indigo-600 text-white cursor-pointer hover:bg-indigo-700' : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`}>
            <FileCode size={20} />
            Upload Tally XML
            <input
              type="file"
              accept=".xml"
              className="hidden"
              disabled={!xmlParty}
              onChange={handleXmlUpload}
            />
          </label>
        </div>
      </div>

      {entries.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-5 border-b border-slate-100 bg-indigo-50/50 flex justify-between items-center">
            <h2 className="font-bold text-indigo-900 flex items-center gap-2">
              <FileJson className="text-indigo-500" size={20} />
              New Entries to Review
            </h2>
            <button 
              onClick={handleSave} 
              disabled={isSaving}
              className="btn-primary py-2 px-4 flex items-center gap-2"
            >
              <Save size={18} />
              {isSaving ? 'Saving...' : 'Confirm & Save'}
            </button>
          </div>
          <CreditEntryReviewTable
            entries={entries}
            onChangeField={handleEntryFieldChange}
            onDelete={(id) => setEntries(entries.filter(e => e.id !== id))}
          />
        </div>
      )}

      {/* Saved Entries */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mt-6">
        <div className="p-5 border-b border-slate-100 flex justify-between items-center">
          <h2 className="font-bold text-slate-800">Saved Entries for {selectedDate}</h2>
          <div className="text-sm font-medium text-slate-500">
            Total: <span className="text-indigo-600 text-lg font-bold">₹{savedEntries.reduce((sum, e) => sum + Number(e.amount), 0).toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
          </div>
        </div>
        
        {savedEntries.length === 0 ? (
          <div className="p-10 text-center text-slate-500">
            No credit sales saved for this date.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-sm border-b border-slate-200">
                  <th className="p-4 font-medium">Party Name</th>
                  <th className="p-4 font-medium">Vehicle No.</th>
                  <th className="p-4 font-medium">Voucher No.</th>
                  <th className="p-4 font-medium">Item</th>
                  <th className="p-4 font-medium text-right">Qty (Ltr)</th>
                  <th className="p-4 font-medium text-right">Amount (₹)</th>
                  <th className="p-4 font-medium text-right">Cash (₹)</th>
                  <th className="p-4 font-medium text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {savedEntries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="p-4 font-medium text-slate-900">{entry.party_name}</td>
                    <td className="p-4 text-slate-600 font-mono">{entry.vehicle_no || '-'}</td>
                    <td className="p-4 text-slate-600 font-mono">{entry.voucher_no || '-'}</td>
                    <td className="p-4 text-slate-600">{entry.item_name}</td>
                    <td className="p-4 text-slate-700 text-right font-mono">{entry.quantity ? Number(entry.quantity).toFixed(2) : '-'}</td>
                    <td className="p-4 text-slate-900 text-right font-mono font-medium">{Number(entry.amount).toLocaleString('en-IN', {minimumFractionDigits: 2})}</td>
                    <td className="p-4 text-amber-700 text-right font-mono font-medium">{entry.cash_amount ? Number(entry.cash_amount).toLocaleString('en-IN', {minimumFractionDigits: 2}) : '-'}</td>
                    <td className="p-4 text-center">
                      <button
                        onClick={() => deleteEntry(entry.id)}
                        className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreditSales;
